import React__default, { useState } from 'react';
import { Button } from '../Button/Button.js';
import { Datepicker } from '../Datepicker/Datepicker.js';
import { Field } from '../Field/Field.js';
import { Input } from '../Input/Input.js';
import { LayoutGrid } from '../LayoutGrid/LayoutGrid.js';
import { LayoutGridColumn } from '../LayoutGrid/LayoutGridColumn.js';
import { Modal } from '../Modal/Modal.js';
import { Select } from '../Select/Select.js';
import LocaleUtils from 'react-day-picker/moment';
import { determineDataTestIdPrefix, getBaseProps, getAriaProps } from '../../../helpers/propHelpers.js';

const drawTypesKeys = {
    prepaid: 1,
    closing: 2,
    first: 3,
    default: 4,
    final: 5,
    correction: 6,
};
const drawTypesOptions = [
    { text: 'First', value: 3 },
    { text: 'Default', value: 4 },
    { text: 'Final', value: 5 },
    { text: 'Correction', value: 6 },
];
const DrawModalLabels = {
    cancelButton: 'Cancel',
    drawNameLabel: 'Draw Name',
    drawNumberLabel: 'Draw Number',
    drawToCorrect: 'Draw to Correct',
    drawType: 'Draw Type',
    modalTitle: 'Create Draw',
    primaryButton: 'Continue to Draw',
    requestDateDescription: 'The date this draw was requested or created.',
    requestDateLabel: 'Request Date',
    transferDateDescription: 'Indicate the date this draw was or will be transferred.',
    transferDateLabel: 'Transfer Date',
    transferDateTooltip: 'Setting the transfer date allows you to back-date a draw, or provide a known funding date for planning and reporting purposes. If no transfer date is provided, it will be set to the date the draw is transferred.',
};
var DrawTypes;
(function (DrawTypes) {
    DrawTypes["PREPAID"] = "prepaid";
    DrawTypes["CLOSING"] = "closing";
    DrawTypes["FIRST"] = "first";
    DrawTypes["DEFAULT"] = "default";
    DrawTypes["FINAL"] = "final";
    DrawTypes["CORRECTION"] = "correction";
})(DrawTypes || (DrawTypes = {}));
const DrawModal = (props) => {
    var _a, _b;
    const DATE_FORMAT = 'MM/DD/YYYY';
    const DATE_PLACEHOLDER = 'mm/dd/yyyy';
    const TIME_FORMAT = 'HH:mm';
    const labels = Object.assign(DrawModalLabels, props.labels);
    const dataTestId = determineDataTestIdPrefix(props, 'create-draw-modal');
    const baseProps = getBaseProps(props);
    const ariaProps = getAriaProps(props);
    const defaultCreatedAt = new Date();
    labels.requestDateDescription = props.requestDateTimestampEnabled
        ? 'The date and time this draw was requested or created.'
        : labels.requestDateDescription;
    const [draw, setDraw] = useState({
        drawName: (_a = props.drawName) !== null && _a !== void 0 ? _a : '',
        drawNumber: props.drawNumber,
        drawToCorrectSelected: props.drawToCorrectSelected,
        drawTypeId: props.drawTypeId,
        createdAt: (_b = props.requestDate) !== null && _b !== void 0 ? _b : LocaleUtils.formatDate(defaultCreatedAt, DATE_FORMAT),
        transactionDate: props.transferDate,
    });
    const [drawSubmittedTime, setDrawSubmittedTime] = useState(LocaleUtils.formatDate(defaultCreatedAt, TIME_FORMAT));
    const isCorrectiveDraw = draw.drawTypeId === props.drawTypesKeys.correction;
    const handleNumberFieldUpdated = (event) => updateDrawProperty(event.target.name, parseInt(event.target.value));
    const handleFieldUpdated = (event) => updateDrawProperty(event.target.name, event.target.value);
    const updateDrawProperty = (property, value) => {
        return setDraw(Object.assign(Object.assign({}, draw), { [property]: value }));
    };
    const drawTypeSelect = (React__default.createElement(Field, { label: labels.drawType, required: true },
        React__default.createElement(Select, { arialabel: 'draw-type', name: "drawTypeId", required: true, defaultValue: props.drawTypeId, className: 'block', handleChange: handleNumberFieldUpdated, "data-testid": `${dataTestId}-draw-type`, options: props.drawTypesOptions })));
    const drawToCorrectSelect = (React__default.createElement(Field, { label: labels.drawToCorrect, required: true },
        React__default.createElement(Select, { arialabel: 'draw-to-correct', className: 'block', "data-testid": `${dataTestId}-draw-number-select`, disabled: !Number.isInteger(draw.drawToCorrectSelected), handleChange: handleNumberFieldUpdated, name: "drawToCorrectSelected", options: props.drawsToCorrectOptions, required: true, value: draw.drawToCorrectSelected })));
    const drawNumberInputText = (React__default.createElement(Field, { label: labels.drawNumberLabel, required: true },
        React__default.createElement(Input, { value: props.drawNumber, placeholder: "1", disabled: true, "data-testid": `${dataTestId}-draw-number-input`, "aria-label": 'draw-number' })));
    const drawNameInputText = (React__default.createElement(Field, { label: labels.drawNameLabel, "aria-label": 'draw-name-label' },
        React__default.createElement(Input, { "aria-labelledby": 'draw-name-label', "aria-label": 'draw-name', name: "drawName", handleChange: handleFieldUpdated, "data-testid": `${dataTestId}-draw-number-name`, defaultValue: draw.drawName })));
    /** create_at in draws table */
    const requestDatePicker = (React__default.createElement(Field, { label: labels.requestDateLabel, description: labels.requestDateDescription },
        React__default.createElement("div", { className: "flex" },
            React__default.createElement(Datepicker, { "aria-label": 'created-date', name: "createdAt", value: draw.createdAt, placeholder: DATE_PLACEHOLDER, disabledAfter: new Date(), handleDayClick: (_event, day) => {
                    updateDrawProperty('createdAt', LocaleUtils.formatDate(day, DATE_FORMAT));
                }, handleDayChange: (day, isDateError) => {
                    return day === undefined
                        ? !isDateError && updateDrawProperty('createdAt', null)
                        : !isDateError && updateDrawProperty('createdAt', LocaleUtils.formatDate(day, DATE_FORMAT));
                }, "data-testid": `${dataTestId}-request-date`, className: "margin-right-xs flex--grow" }),
            props.requestDateTimestampEnabled && (React__default.createElement(Input, { "aria-labelledby": 'draw-submitted-time-input', "aria-label": 'draw-submitted-time', name: "drawSubmittedTime", handleChange: (event) => setDrawSubmittedTime(event.target.value), "data-testid": `${dataTestId}-request-time-input`, defaultValue: drawSubmittedTime, type: 'time' })))));
    const transferDatePicker = (React__default.createElement(Field, { label: labels.transferDateLabel, description: labels.transferDateDescription, tooltip: labels.transferDateTooltip },
        React__default.createElement(Datepicker, { "aria-label": 'transferred-date', name: "transactionDate", placeholder: DATE_PLACEHOLDER, handleDayClick: (_event, day) => {
                updateDrawProperty('transactionDate', LocaleUtils.formatDate(day, DATE_FORMAT));
            }, handleDayChange: (day, isDateError) => {
                return day === undefined
                    ? !isDateError && updateDrawProperty('transactionDate', null)
                    : !isDateError && updateDrawProperty('transactionDate', LocaleUtils.formatDate(day, DATE_FORMAT));
            }, "data-testid": `${dataTestId}-transferred-date`, value: draw.transactionDate })));
    const cancelButton = (React__default.createElement(Button, { "aria-label": 'cancel-draw', type: "secondary", handleClick: props.onModalClosed, key: "secondary", "data-testid": `${dataTestId}-cancel-button` }, labels.cancelButton));
    const submitButton = (React__default.createElement(Button, { "aria-label": 'submit-draw', className: `create-draw-modal__btn-continue-to-draw`, type: "primary", handleClick: () => {
            const updatedDraw = Object.assign({}, draw);
            if (!draw.createdAt) {
                updatedDraw.createdAt = LocaleUtils.formatDate(defaultCreatedAt);
            }
            else {
                const formattedCreatedAt = draw.createdAt + ' ' + drawSubmittedTime;
                const utcFormattedDate = new Date(formattedCreatedAt).toISOString();
                updatedDraw.requestDate = utcFormattedDate;
            }
            props.onSubmit(updatedDraw);
        }, key: "primary", "data-testid": `${dataTestId}-primary-button` }, labels.primaryButton));
    return (React__default.createElement(Modal, Object.assign({}, ariaProps, baseProps, { isVisible: props.isVisible, title: labels.modalTitle, width: "medium", className: `DrawModal ${baseProps.className}`, collapseModalBody: true, footerItemsRight: [cancelButton, submitButton], handleClose: props.onModalClosed, "data-testid": props['data-testid'], trapFocus: false }),
        React__default.createElement("div", { className: "DrawModal__container" },
            React__default.createElement(LayoutGrid, null,
                React__default.createElement(LayoutGridColumn, { size: 4 }, drawTypeSelect)),
            React__default.createElement(LayoutGrid, null,
                React__default.createElement(LayoutGridColumn, { size: 4 }, isCorrectiveDraw ? drawToCorrectSelect : drawNumberInputText),
                React__default.createElement(LayoutGridColumn, { size: 8 }, drawNameInputText)),
            React__default.createElement(LayoutGrid, null,
                React__default.createElement(LayoutGridColumn, { size: 6 },
                    React__default.createElement("div", { className: "DrawModal__request-date" }, requestDatePicker)),
                React__default.createElement(LayoutGridColumn, { size: 6 },
                    React__default.createElement("div", { className: "DrawModal__transferred-date" }, transferDatePicker))))));
};

export { DrawModal, DrawModalLabels, DrawTypes, DrawModal as default, drawTypesKeys, drawTypesOptions };
//# sourceMappingURL=DrawModal.js.map
